import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ChevronLeft, ChevronRight, Calendar, TrendingUp, DollarSign, 
  Fuel, Plus, Download, RefreshCw, Edit2, Trash2 
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { getDailyOperations, createManualRide, createFuelPurchase, deleteFuelPurchase, editFuelPurchase, editBooking, deleteBooking } from '../utils/api';
import ManualRideForm from '../components/ManualRideForm';
import FuelPurchaseForm from '../components/FuelPurchaseForm';
import FuelPurchaseList from '../components/FuelPurchaseList';
import { formatTime12Hour } from '../utils/timeFormat';

const DailyTrackerView = () => {
  const { date } = useParams();
  const navigate = useNavigate();
  
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showManualRideForm, setShowManualRideForm] = useState(false);
  const [showFuelForm, setShowFuelForm] = useState(false);
  const [editingFuelPurchase, setEditingFuelPurchase] = useState(null);
  const [editingRide, setEditingRide] = useState(null);

  useEffect(() => {
    if (date) {
      fetchDailyData();
    }
  }, [date]);

  const fetchDailyData = async () => {
    try {
      setLoading(true);
      const data = await getDailyOperations(date);
      setDailyData(data);
    } catch (error) {
      console.error('Error fetching daily operations:', error);
      alert('Failed to load daily operations data');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousDay = () => {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() - 1);
    navigate(`/admin/daily/${currentDate.toISOString().split('T')[0]}`);
  };

  const handleNextDay = () => {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() + 1);
    navigate(`/admin/daily/${currentDate.toISOString().split('T')[0]}`);
  };

  const handleManualRideSubmit = async (rideData) => {
    try {
      if (editingRide && editingRide._bookingId) {
        // Editing existing ride
        await editBooking(editingRide._bookingId, {
          passengerName: rideData.passengerName,
          passengerPhone: rideData.passengerPhone,
          pickupLocation: rideData.pickupLocation,
          dropoffLocation: rideData.dropoffLocation,
          serviceType: rideData.serviceType,
          date: rideData.date,
          time: rideData.time,
          actualFare: rideData.actualFare,
          paymentMethod: rideData.paymentMethod,
          passengers: rideData.passengers,
          luggage: rideData.luggage,
          extraActivities: rideData.extraActivities,
          specialRequests: rideData.notes,
          bookingSource: rideData.bookingSource
        });
        setEditingRide(null);
      } else {
        // Creating new ride
        await createManualRide(rideData);
      }
      await fetchDailyData();
      setShowManualRideForm(false);
    } catch (error) {
      throw error; // Let form handle the error
    }
  };

  const handleFuelPurchaseSubmit = async (fuelData) => {
    try {
      if (editingFuelPurchase) {
        await editFuelPurchase(editingFuelPurchase.id, fuelData);
        setEditingFuelPurchase(null);
      } else {
        await createFuelPurchase(fuelData);
      }
      await fetchDailyData();
      setShowFuelForm(false);
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteFuel = async (fuelId) => {
    if (!window.confirm('Are you sure you want to delete this fuel purchase?')) {
      return;
    }

    try {
      await deleteFuelPurchase(fuelId);
      await fetchDailyData();
    } catch (error) {
      alert('Failed to delete fuel purchase');
    }
  };

  const handleEditFuel = (fuel) => {
    setEditingFuelPurchase(fuel);
    setShowFuelForm(true);
  };

  const handleEditRide = (ride) => {
    // Convert booking format to ManualRideCreate format for the form
    setEditingRide({
      passengerName: ride.passengerName,
      passengerPhone: ride.passengerPhone,
      pickupLocation: ride.pickupLocation,
      dropoffLocation: ride.dropoffLocation,
      serviceType: ride.serviceType || 'other',
      date: ride.date,
      time: ride.time,
      actualFare: ride.actualFare || ride.estimatedFare,
      paymentMethod: ride.paymentMethod,
      passengers: ride.passengers,
      luggage: ride.luggage,
      extraActivities: ride.extraActivities || [],
      notes: ride.specialRequests || '',
      bookingSource: ride.bookingSource || 'manual',
      _bookingId: ride.id // Store original ID for update
    });
    setShowManualRideForm(true);
  };

  const handleDeleteRide = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this ride? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteBooking(bookingId);
      await fetchDailyData();
    } catch (error) {
      console.error('Error deleting ride:', error);
      alert('Failed to delete ride: ' + (error.response?.data?.detail || error.message));
    }
  };

  const formatDateDisplay = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'Scheduled':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'Cancelled':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    }
  };

  const filterBookings = (bookings) => {
    if (!bookings) return [];
    switch (activeTab) {
      case 'scheduled':
        return bookings.filter(b => b.status === 'Scheduled' || b.status === 'Assigned');
      case 'completed':
        return bookings.filter(b => b.status === 'Completed');
      case 'cancelled':
        return bookings.filter(b => b.status === 'Cancelled');
      default:
        return bookings;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mb-4"></div>
          <p className="text-gray-600">Loading daily operations...</p>
        </div>
      </div>
    );
  }

  if (!dailyData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600">No data available for this date</p>
          <button
            onClick={() => navigate('/admin')}
            className="mt-4 px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { summary, paymentBreakdown, sourceBreakdown, bookings, fuelPurchases } = dailyData;
  const filteredBookings = filterBookings(bookings);

  return (
    <>
      <Helmet>
        <title>Daily Operations - {date} | Taxi-98 Admin</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pb-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center space-x-2 text-white hover:text-gray-200 mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">Daily Operations Tracker</h1>
                <p className="text-blue-100">{formatDateDisplay(date)}</p>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={handlePreviousDay}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                <button
                  onClick={() => navigate(`/admin/daily/${new Date().toISOString().split('T')[0]}`)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Calendar className="w-5 h-5" />
                  <span>Today</span>
                </button>

                <button
                  onClick={handleNextDay}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                <button
                  onClick={fetchDailyData}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Summary Statistics */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <TrendingUp className="w-6 h-6 mr-2 text-cyan-600" />
              Daily Summary
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Rides */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <div className="text-sm text-gray-600 mb-1">Total Rides</div>
                <div className="text-3xl font-bold text-gray-900">{summary.totalRides}</div>
                <div className="text-xs text-gray-500 mt-2">
                  {summary.completedRides} completed • {summary.scheduledRides} scheduled
                </div>
              </div>

              {/* Total Earnings */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <div className="text-sm text-gray-600 mb-1">Total Earnings</div>
                <div className="text-3xl font-bold text-green-600 flex items-center">
                  <DollarSign className="w-7 h-7" />
                  {summary.totalEarnings.toFixed(2)}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  From {summary.completedRides} completed rides
                </div>
              </div>

              {/* Fuel Spent */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <div className="text-sm text-gray-600 mb-1">Fuel Spent</div>
                <div className="text-3xl font-bold text-orange-600 flex items-center">
                  <DollarSign className="w-7 h-7" />
                  {summary.totalFuelCost.toFixed(2)}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  {fuelPurchases?.length || 0} fuel {fuelPurchases?.length === 1 ? 'purchase' : 'purchases'}
                </div>
              </div>

              {/* Net Revenue */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                <div className="text-sm text-gray-600 mb-1">Net After Fuel</div>
                <div className={`text-3xl font-bold flex items-center ${summary.netRevenue >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                  <DollarSign className="w-7 h-7" />
                  {summary.netRevenue.toFixed(2)}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Earnings minus fuel costs
                </div>
              </div>
            </div>

            {/* Payment & Source Breakdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {/* Payment Breakdown */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Payment Breakdown</h3>
                {Object.keys(paymentBreakdown).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(paymentBreakdown).map(([method, data]) => (
                      <div key={method} className="flex justify-between items-center">
                        <span className="text-gray-700">{method}</span>
                        <span className="font-semibold text-gray-900">
                          ${data.total.toFixed(2)} ({data.count})
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No completed rides yet</p>
                )}
              </div>

              {/* Booking Sources */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Booking Sources</h3>
                {Object.keys(sourceBreakdown).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(sourceBreakdown).map(([source, data]) => (
                      <div key={source} className="flex justify-between items-center">
                        <span className="text-gray-700 capitalize">{source.replace('-', ' ')}</span>
                        <span className="font-semibold text-gray-900">
                          {data.count} {data.count === 1 ? 'ride' : 'rides'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No bookings yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mb-8">
            <button
              onClick={() => setShowManualRideForm(true)}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg font-semibold"
            >
              <Plus className="w-5 h-5" />
              <span>Add Completed Ride</span>
            </button>

            <button
              onClick={() => setShowFuelForm(true)}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all shadow-lg font-semibold"
            >
              <Fuel className="w-5 h-5" />
              <span>Add Fuel Purchase</span>
            </button>
          </div>

          {/* Rides Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Rides</h2>

            {/* Tabs */}
            <div className="flex space-x-2 mb-6 border-b border-gray-200">
              {[
                { id: 'all', label: 'All', count: bookings.length },
                { id: 'completed', label: 'Completed', count: summary.completedRides },
                { id: 'scheduled', label: 'Scheduled', count: summary.scheduledRides },
                { id: 'cancelled', label: 'Cancelled', count: summary.cancelledRides }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'border-cyan-500 text-cyan-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            {/* Rides List */}
            {filteredBookings.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No rides in this category</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredBookings.map(booking => (
                  <div
                    key={booking.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="font-semibold text-gray-900">{formatTime12Hour(booking.time)}</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadgeColor(booking.status)}`}>
                            {booking.status}
                          </span>
                          {booking.bookingSource && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs capitalize">
                              {booking.bookingSource.replace('-', ' ')}
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="text-sm text-gray-600">Customer</div>
                            <div className="font-medium text-gray-900">{booking.passengerName}</div>
                            <div className="text-sm text-gray-500">{booking.passengerPhone}</div>
                          </div>

                          <div>
                            <div className="text-sm text-gray-600">Locations</div>
                            <div className="text-sm text-gray-900">
                              <div>{booking.pickupLocation}</div>
                              <div className="text-gray-500">→ {booking.dropoffLocation}</div>
                            </div>
                          </div>

                          <div>
                            <div className="text-sm text-gray-600">Details</div>
                            <div className="text-sm text-gray-900">
                              {booking.passengers} passengers • {booking.luggage} luggage
                            </div>
                          </div>

                          <div>
                            <div className="text-sm text-gray-600">Fare • Payment</div>
                            <div className="font-semibold text-green-600">
                              ${(booking.actualFare || booking.finalFare || booking.estimatedFare).toFixed(2)} • {booking.paymentMethod}
                            </div>
                          </div>
                        </div>

                        {booking.extraActivities && booking.extraActivities.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-sm text-gray-600 mb-1">Extra Activities</div>
                            <div className="flex flex-wrap gap-2">
                              {booking.extraActivities.map((activity, idx) => (
                                <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                                  {activity.type.replace('-', ' ')}: ${activity.cost.toFixed(2)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {booking.specialRequests && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-sm text-gray-600 mb-1">Notes</div>
                            <div className="text-sm text-gray-700">{booking.specialRequests}</div>
                          </div>
                        )}
                      </div>

                      {/* Edit/Delete Controls for Manual Rides */}
                      {booking.bookingSource === 'manual' && (
                        <div className="flex flex-col space-y-2 ml-4">
                          <button
                            onClick={() => handleEditRide(booking)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit Ride"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRide(booking.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Ride"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Fuel Purchases Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <Fuel className="w-6 h-6 mr-2 text-orange-600" />
                Fuel Purchases
              </h2>
            </div>

            <FuelPurchaseList
              fuelPurchases={fuelPurchases}
              onEdit={handleEditFuel}
              onDelete={handleDeleteFuel}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <ManualRideForm
        isOpen={showManualRideForm}
        onClose={() => setShowManualRideForm(false)}
        onSubmit={handleManualRideSubmit}
        selectedDate={date}
      />

      <FuelPurchaseForm
        isOpen={showFuelForm}
        onClose={() => {
          setShowFuelForm(false);
          setEditingFuelPurchase(null);
        }}
        onSubmit={handleFuelPurchaseSubmit}
        selectedDate={date}
        editData={editingFuelPurchase}
      />
    </>
  );
};

export default DailyTrackerView;
